/**
 * Note and task intents shared by every detail route.
 *
 * Each detail screen owns one action; it delegates the panel intents here and
 * handles only what is specific to its own entity. Returns `null` when the
 * intent is not one of these, so the caller can carry on.
 */

import type { SubjectRef, SubjectType, TaskStatus } from '@erp/contracts'
import { api, ApiError } from './api'

export interface ActionResult {
  ok: boolean
  message?: string
  issues?: Record<string, string[]>
}

const SUBJECT_TYPES: readonly SubjectType[] = ['lead', 'deal', 'organization', 'contact']

function readSubject(form: FormData): SubjectRef {
  const type = String(form.get('subjectType'))
  const id = String(form.get('subjectId'))
  if (!SUBJECT_TYPES.includes(type as SubjectType)) {
    throw new Response(`Unknown subject type: ${type}`, { status: 400 })
  }
  return { type: type as SubjectType, id }
}

/** Empty string means "field left blank", which the contracts treat as null. */
function optional(form: FormData, key: string): string | null {
  const value = form.get(key)
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

export async function handleSubjectIntent(
  intent: string,
  form: FormData,
): Promise<ActionResult | null> {
  try {
    switch (intent) {
      case 'note.create': {
        await api.notes.create({
          subject: readSubject(form),
          title: optional(form, 'title'),
          body: String(form.get('body') ?? ''),
        })
        return { ok: true }
      }

      case 'note.delete': {
        await api.notes.remove(String(form.get('noteId')))
        return { ok: true }
      }

      case 'note.restore': {
        await api.notes.restore(String(form.get('noteId')))
        return { ok: true }
      }

      case 'task.create': {
        const due = optional(form, 'dueDate')
        await api.tasks.create({
          subject: readSubject(form),
          title: String(form.get('title') ?? ''),
          description: optional(form, 'description'),
          status: 'todo',
          priority: (optional(form, 'priority') ?? 'medium') as 'low' | 'medium' | 'high',
          // <input type="datetime-local"> yields a local wall-clock string with
          // no zone; the contracts want an instant.
          dueDate: due ? new Date(due).toISOString() : null,
          assignedTo: optional(form, 'assignedTo'),
        })
        return { ok: true }
      }

      case 'task.status': {
        await api.tasks.update(String(form.get('taskId')), {
          status: String(form.get('status')) as TaskStatus,
        })
        return { ok: true }
      }

      case 'task.assign': {
        // Empty string means "unassign", which the contract spells as null.
        await api.tasks.update(String(form.get('taskId')), {
          assignedTo: optional(form, 'assignedTo'),
        })
        return { ok: true }
      }

      case 'assign': {
        // Checkboxes post one entry per selection; none checked means "clear".
        const userIds = form.getAll('userId').map(String)
        await api.assignments.set(readSubject(form), userIds)
        return { ok: true }
      }

      case 'task.delete': {
        await api.tasks.remove(String(form.get('taskId')))
        return { ok: true }
      }

      case 'task.restore': {
        await api.tasks.restore(String(form.get('taskId')))
        return { ok: true }
      }

      default:
        return null
    }
  } catch (error) {
    if (error instanceof ApiError) {
      return { ok: false, message: error.message, issues: error.issues }
    }
    throw error
  }
}
